/**
 * User service with consistent API response format and full TypeScript support
 * Provides comprehensive user management functionality including CRUD operations,
 * pagination, search, and batch operations.
 */

import api from "./apiService";
import { ApiResponse, User } from "../types/apiTypes";
import { parseConsistentApiResponse } from "../utils/consistentApiParser";

class UserService {
  /**
   * Get all users
   */
  async getAll(): Promise<ApiResponse<User[]>> {
    if (process.env.NODE_ENV === "development") {
      console.log("userService: Fetching all users...");
    }

    const response = await api.get("/users");
    return parseConsistentApiResponse<User[]>(response, "userService", "getAll");
  }

  /**
   * Get user by ID
   */
  async getById(id: string): Promise<ApiResponse<User>> {
    if (process.env.NODE_ENV === "development") {
      console.log(`userService: Fetching user by ID: ${id}`);
    }

    const response = await api.get(`/users/${id}`);
    return parseConsistentApiResponse<User>(response, "userService", "getById");
  }

  /**
   * Create new user
   */
  async create(userData: Partial<User>): Promise<ApiResponse<User>> {
    if (process.env.NODE_ENV === "development") {
      console.log("userService: Creating user with data:", userData);
    }

    // Ensure access_tags is properly formatted
    const userDataToSend = { ...userData };
    if (userDataToSend.access_tags) {
      const formattedAccessTags: Record<string, any> = {};
      Object.entries(userDataToSend.access_tags).forEach(([key, value]) => {
        formattedAccessTags[String(key)] = value;
      });
      userDataToSend.access_tags = formattedAccessTags;
    }

    const response = await api.post("/users", userDataToSend);
    return parseConsistentApiResponse<User>(response, "userService", "create");
  }

  /**
   * Submit an account request
   */
  async submitAccountRequest(requestData: {
    fullName: string;
    email: string;
    organization: string;
    requestedRole: string;
    reason: string;
    phone?: string;
  }): Promise<ApiResponse<any>> {
    if (process.env.NODE_ENV === "development") {
      console.log("userService: Submitting account request:", requestData);
    }

    const response = await api.post("/account-requests", requestData);
    return parseConsistentApiResponse<any>(response, "userService", "submitAccountRequest");
  }
  
  /**
   * Update user
   */
  async update(id: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    if (process.env.NODE_ENV === "development") {
      console.log(`userService: Updating user ${id} with data:`, userData);
    }

    // Ensure access_tags is properly formatted
    const userDataToSend = { ...userData };
    if (userDataToSend.access_tags) {
      const formattedAccessTags: Record<string, any> = {};
      Object.entries(userDataToSend.access_tags).forEach(([key, value]) => {
        formattedAccessTags[String(key)] = value;
      });
      userDataToSend.access_tags = formattedAccessTags;
    }

    const response = await api.put(`/users/${id}`, userDataToSend);
    return parseConsistentApiResponse<User>(response, "userService", "update");
  }

  /**
   * Delete user
   */
  async remove(id: string): Promise<ApiResponse<{ user_id: string }>> {
    if (process.env.NODE_ENV === "development") {
      console.log(`userService: Deleting user: ${id}`);
    }

    const response = await api.delete(`/users/${id}`);
    return parseConsistentApiResponse<{ user_id: string }>(response, "userService", "remove");
  }

  /**
   * Get users with pagination
   */
  async getPaginated(page: number = 1, perPage: number = 20): Promise<ApiResponse<User[]>> {
    if (process.env.NODE_ENV === "development") {
      console.log(`userService: Fetching users page ${page} with ${perPage} items per page`);
    }

    const response = await api.get("/users", {
      params: {
        page,
        per_page: perPage,
      },
    });

    return parseConsistentApiResponse<User[]>(response, "userService", "getPaginated");
  }

  /**
   * Search users by username or email
   */
  async search(query: string, page: number = 1, perPage: number = 20): Promise<ApiResponse<User[]>> {
    if (process.env.NODE_ENV === "development") {
      console.log(`userService: Searching users with query: ${query}`);
    }

    const response = await api.get("/users/search", {
      params: {
        q: query,
        page,
        per_page: perPage,
      },
    });

    return parseConsistentApiResponse<User[]>(response, "userService", "search");
  }

  /**
   * Get users by role
   */
  async getByRole(role: string, page: number = 1, perPage: number = 20): Promise<ApiResponse<User[]>> {
    if (process.env.NODE_ENV === "development") {
      console.log(`userService: Fetching users with role: ${role}`);
    }

    const response = await api.get("/users", {
      params: {
        role,
        page,
        per_page: perPage,
      },
    });

    return parseConsistentApiResponse<User[]>(response, "userService", "getByRole");
  }

  /**
   * Get all resources a user has permissions for
   */
  async getUserPermissions(userId: string): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/users/${userId}/permissions`);
    return parseConsistentApiResponse<any[]>(response, "userService", "getUserPermissions");
  }

  /**
   * Grant/update a user's permission to a resource (ADMIN only)
   */
  async setUserPermission(userId: string, resourceId: string, resourceType: string): Promise<ApiResponse<any>> {
    const response = await api.put(`/users/${userId}/permissions/${resourceId}`, { resource_type: resourceType });
    return parseConsistentApiResponse<any>(response, "userService", "setUserPermission");
  }

  /**
   * Remove a user's permission to a resource (ADMIN only)
   */
  async removeUserPermission(userId: string, resourceId: string): Promise<ApiResponse<any>> {
    const response = await api.delete(`/users/${userId}/permissions/${resourceId}`);
    return parseConsistentApiResponse<any>(response, "userService", "removeUserPermission");
  }

  /**
   * Batch update users
   */
  async batchUpdate(updates: Array<{ id: string; data: Partial<User> }>): Promise<ApiResponse<User[]>> {
    if (process.env.NODE_ENV === "development") {
      console.log("userService: Batch updating users:", updates);
    }

    const response = await api.put("/users/batch", { updates });
    return parseConsistentApiResponse<User[]>(response, "userService", "batchUpdate");
  }

  /**
   * Batch delete users
   */
  async batchDelete(userIds: string[]): Promise<ApiResponse<{ deleted_count: number; deleted_ids: string[] }>> {
    if (process.env.NODE_ENV === "development") {
      console.log("userService: Batch deleting users:", userIds);
    }

    const response = await api.delete("/users/batch", {
      data: { user_ids: userIds },
    });

    return parseConsistentApiResponse<{ deleted_count: number; deleted_ids: string[] }>(
      response,
      "userService",
      "batchDelete",
    );
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;
